
import { reputationApi } from '../lib/api'

// Point values for different actions
export const REPUTATION_REWARDS = {
  // Content creation
  create_post: 10,
  create_comment: 3,
  create_review: 15,

  // Social interactions
  receive_upvote: 5,
  receive_comment: 2,
  gain_follower: 5,

  // Community participation
  join_group: 2,
  create_group: 20,
  share_content: 1,

  // Daily engagement
  daily_login: 1,
  first_post_of_day: 5,

  // Special achievements
  post_featured: 50,
  reach_100_followers: 100,
  reach_500_followers: 250,
} as const

export type ReputationAction = keyof typeof REPUTATION_REWARDS

interface AwardResult {
  success: boolean
  pointsAwarded: number
  newTotal: number
  error?: string
}

export const useReputationRewards = () => {
  const awardPoints = async (
    userId: string,
    action: ReputationAction,
    customPoints?: number
  ): Promise<AwardResult> => {
    try {
      const points = customPoints ?? REPUTATION_REWARDS[action]

      // Award points via API
      const response = await reputationApi.awardPoints(userId, action, customPoints)

      if (!response.success) {
        return {
          success: false,
          pointsAwarded: 0,
          newTotal: 0,
          error: response.error || 'Failed to award points',
        }
      }

      return {
        success: true,
        pointsAwarded: response.data?.pointsAwarded || points,
        newTotal: response.data?.newTotal || 0,
      }
    } catch (error) {
      console.error('Error awarding reputation:', error)
      return {
        success: false,
        pointsAwarded: 0,
        newTotal: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  const getPointsForAction = (action: ReputationAction): number => {
    return REPUTATION_REWARDS[action]
  }

  const getRewardDescription = (action: ReputationAction): string => {
    const descriptions: Record<ReputationAction, string> = {
      create_post: 'Creating a new post',
      create_comment: 'Commenting on a post',
      create_review: 'Writing a drama review',
      receive_upvote: 'Receiving an upvote',
      receive_comment: 'Someone commented on your post',
      gain_follower: 'Gaining a new follower',
      join_group: 'Joining a group',
      create_group: 'Creating a new group',
      share_content: 'Sharing content',
      daily_login: 'Daily login bonus',
      first_post_of_day: 'First post of the day',
      post_featured: 'Your post was featured',
      reach_100_followers: 'Reached 100 followers',
      reach_500_followers: 'Reached 500 followers',
    }
    return descriptions[action]
  }

  return {
    awardPoints,
    getPointsForAction,
    getRewardDescription,
    REPUTATION_REWARDS,
  }
}

// Standalone function for use outside React - calls API
export const awardReputationPoints = async (
  userId: string,
  action: ReputationAction
): Promise<AwardResult> => {
  try {
    const points = REPUTATION_REWARDS[action]

    const response = await reputationApi.awardPoints(userId, action)

    if (!response.success) {
      return {
        success: false,
        pointsAwarded: 0,
        newTotal: 0,
        error: response.error || 'Failed to award points',
      }
    }

    return {
      success: true,
      pointsAwarded: response.data?.pointsAwarded || points,
      newTotal: response.data?.newTotal || 0,
    }
  } catch (error) {
    console.error('Error awarding reputation:', error)
    return {
      success: false,
      pointsAwarded: 0,
      newTotal: 0,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}
